import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { marked } from "marked";
import Header from "../../components/Header";
import "./style.css";

// 标签配置（与 HelpCenter 一致，去掉"必看"后缀）
const TAB_CONFIG = [
  { key: "秒应核心功能", label: "秒应核心功能", color: "#3b82f6" },
  { key: "老师班级管理", label: "老师班级管理", color: "#7c6fae" },
  { key: "行政企业管理", label: "行政企业管理", color: "#0d9488" },
  { key: "大学班委统计", label: "大学班委统计", color: "#e11d48" },
  { key: "社区生活统计", label: "社区生活统计", color: "#ea580c" },
  { key: "团队活动组织", label: "团队活动组织", color: "#16a34a" },
  { key: "商铺社区团购", label: "商铺社区团购", color: "#ca8a04" },
  { key: "机构预约报名", label: "机构预约报名", color: "#db2777" },
];

const HelpCenterDetail = () => {
  const { category, id } = useParams();
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [scenario, setScenario] = useState("");
  const [description, setDescription] = useState("");
  const [keywords, setKeywords] = useState("");

  const currentTab = TAB_CONFIG.find((t) => t.key === category) || TAB_CONFIG[0];

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/scenarios/${category}/${id}.md`);
        if (res.ok) {
          const mdText = await res.text();

          // 解析 frontmatter
          let bodyText = mdText;
          const fmMatch = mdText.match(/^---\s*\n([\s\S]*?)\n---/);
          if (fmMatch) {
            bodyText = mdText.slice(fmMatch[0].length).trim();
            const fm = fmMatch[1];
            const titleMatch = fm.match(/^title:\s*(.+)$/m);
            if (titleMatch) setTitle(titleMatch[1].trim());
            const scenarioMatch = fm.match(/^scenario:\s*(.+)$/m);
            if (scenarioMatch) setScenario(scenarioMatch[1].trim());
            const descMatch = fm.match(/^description:\s*(.+)$/m);
            if (descMatch) setDescription(descMatch[1].trim());
            const kwMatch = fm.match(/^keywords:\s*(.+)$/m);
            if (kwMatch) setKeywords(kwMatch[1].trim());
          } else {
            // 没有 frontmatter，取第一个 # 标题
            const h1Match = mdText.match(/^#\s+(.+)$/m);
            if (h1Match) setTitle(h1Match[1].trim());
            // 如果仍无标题，取第一行非空文本
            if (!h1Match) {
              const firstLine = mdText.split('\n').map(l => l.trim()).find(l => l.length > 0);
              if (firstLine) setTitle(firstLine.replace(/^#+\s*/, ''));
            }
          }

          // 移除 body 中的第一个 h1（避免标题重复）
          bodyText = bodyText.replace(/^#\s+.+\n?/, "").trim();

          // 使用 marked 解析 markdown 为 html
          const htmlContent = marked(bodyText);
          setContent(htmlContent);
        } else {
          setContent("<p>文章加载失败</p>");
        }
      } catch (error) {
        console.error("加载文章失败:", error);
        setContent("<p>文章加载失败</p>");
      } finally {
        setLoading(false);
      }
    };

    if (category && id) {
      fetchArticle();
    }
  }, [category, id]);

  return (
    <div className={`helpcenter-detail-page ${isLoaded ? "loaded" : ""}`}>
      <Header activePath="/helpcenter" />

      {/* 文章头部 */}
      <section className="hcd-hero" style={{ background: `linear-gradient(135deg, ${currentTab.color}12, ${currentTab.color}06)` }}>
        <div className="hcd-hero-inner">
          {/* 返回按钮 + 分类标签 */}
          <div className="hcd-back-row">
            <Link to="/helpcenter" className="hcd-back-btn">
              <ArrowLeftOutlined /> 返回帮助中心
            </Link>
            {currentTab && (
              <span
                className="hcd-category-tag"
                style={{
                  color: currentTab.color,
                  backgroundColor: `${currentTab.color}15`,
                }}
              >
                {currentTab.label}
              </span>
            )}
          </div>

          {/* 标题 */}
          {title && <h1 className="hcd-title">{title}</h1>}

          {/* 元信息行：场景 + 描述 + 关键词 */}
          {(scenario || description || keywords) && (
            <div className="hcd-meta-row">
              {scenario && (
                <div className="hcd-meta-item">
                  <span className="hcd-meta-label">场景</span>
                  <span className="hcd-meta-value">{scenario}</span>
                </div>
              )}
              {description && (
                <div className="hcd-meta-item">
                  <span className="hcd-meta-label">简介</span>
                  <span className="hcd-meta-value">{description}</span>
                </div>
              )}
              {keywords && (
                <div className="hcd-meta-item hcd-meta-keywords">
                  <span className="hcd-meta-label">关键词</span>
                  <div className="hcd-keyword-list">
                    {keywords.split(",").map((kw, i) => (
                      <span key={i} className="hcd-keyword-tag">{kw.trim()}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 以Markdown格式查看按钮 */}
          <a
            href={`/scenarios/${category}/${id}.md`}
            target="_blank"
            rel="noopener noreferrer"
            className="hcd-md-link"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M20.56 18H3.44C2.65 18 2 17.37 2 16.59V7.41C2 6.63 2.65 6 3.44 6h17.12c.79 0 1.44.63 1.44 1.41v9.18c0 .78-.65 1.41-1.44 1.41zM6.81 15.19v-3.66l1.92 2.35 1.92-2.35v3.66h1.93V8.81h-1.93l-1.92 2.35-1.92-2.35H4.88v6.38h1.93zM19.69 12h-1.92V8.81h-1.92V12h-1.93l2.88 3.19L19.69 12z"/>
            </svg>
            以Markdown格式查看
          </a>
        </div>
      </section>

      {/* 文章内容 */}
      <section className="hcd-content-section">
        <div className="hcd-content-inner">
          {loading ? (
            <div className="hcd-loading">
              <div className="hc-loading-spinner" />
              <span>加载中...</span>
            </div>
          ) : (
            <article
              className="hcd-article markdown-body"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </div>
      </section>

      {/* CTA - 现在就试试秒应 */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>现在就试试秒应</h2>
            <p>扫码立即体验，免费使用，无需注册</p>
            <div className="cta-qr-codes">
              <div className="qr-item">
                <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" className="qr-img" />
                <span className="qr-label">微信小程序</span>
              </div>
              <div className="qr-item">
                <img src="/assets/code4.6658546.jpg" alt="网页版二维码" className="qr-img" />
                <span className="qr-label">微信公众号</span>
              </div>
              <div className="qr-item">
                <img src="/assets/code3.563ecfb.jpg" alt="客服微信" className="qr-img" />
                <span className="qr-label">客服微信：jizhitech</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpCenterDetail;
